import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useStore } from '../store';
import type { Account, AccountType } from '../types';

/* ── Design tokens ─────────────────────────────────────────────── */
const G = '#635bff';
const S1 = 'var(--surface)';
const S2 = 'var(--s2)';
const BD = 'var(--bd)';
const T2 = 'var(--t2)';

/* ── Types ──────────────────────────────────────────────────────── */
type Phase =
  | 'setup'
  | 'driver-dashboard'
  | 'bridge-dayrecap'
  | 'driver-dayrecap'
  | 'bridge-analytics'
  | 'driver-analytics'
  | 'bridge-playbook'
  | 'driver-playbook'
  | 'bridge-prophq'
  | 'driver-prophq'
  | 'bridge-settings'
  | 'driver-settings'
  | 'done';

/* ── Small helpers ──────────────────────────────────────────────── */
function Btn({
  children,
  onClick,
  variant = 'primary',
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'ghost';
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '11px 24px',
        borderRadius: 999,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 700,
        fontSize: 14,
        transition: 'opacity .15s',
        ...(variant === 'primary'
          ? { background: G, color: '#fff', opacity: disabled ? 0.5 : 1 }
          : { background: 'transparent', color: T2, border: `1px solid ${BD}` }),
      }}
    >
      {children}
    </button>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, color: T2, fontWeight: 500 }}>{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: S2,
          border: `1px solid ${BD}`,
          borderRadius: 8,
          padding: '10px 14px',
          color: 'var(--t1)',
          fontSize: 14,
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

/* ── Bridge modal between pages ────────────────────────────────── */
function BridgeModal({
  icon,
  title,
  desc,
  btnLabel,
  onNext,
  onSkip,
  isHe,
}: {
  icon: string;
  title: string;
  desc: string;
  btnLabel: string;
  onNext: () => void;
  onSkip: () => void;
  isHe: boolean;
}) {
  return (
    <Overlay isHe={isHe}>
      <div
        style={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div style={{ fontSize: 48 }}>{icon}</div>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px' }}>{title}</h3>
          <p style={{ color: T2, fontSize: 14, margin: 0, lineHeight: 1.6 }}>{desc}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="ghost" onClick={onSkip}>
            {isHe ? 'דלג לסוף' : 'Skip to end'}
          </Btn>
          <Btn onClick={onNext}>{btnLabel}</Btn>
        </div>
      </div>
    </Overlay>
  );
}

/* ── Overlay wrapper ────────────────────────────────────────────── */
function Overlay({ children, isHe }: { children: React.ReactNode; isHe: boolean }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: S1,
          border: `1px solid ${BD}`,
          borderRadius: 20,
          padding: 32,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'var(--t1)',
          boxShadow: '0 24px 80px rgba(0,0,0,.6)',
        }}
        dir={isHe ? 'rtl' : 'ltr'}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Skip button floating over driver.js tour ───────────────────── */
function SkipButton({ onClick, isHe }: { onClick: () => void; isHe: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'fixed',
        bottom: 24,
        right: isHe ? 'auto' : 24,
        left: isHe ? 24 : 'auto',
        zIndex: 100001,
        background: S2,
        border: `1px solid ${BD}`,
        color: T2,
        borderRadius: 999,
        padding: '8px 18px',
        fontSize: 13,
        cursor: 'pointer',
        fontWeight: 600,
      }}
    >
      {isHe ? 'דלג על הסיור' : 'Skip tour'}
    </button>
  );
}

/* ── Main component ─────────────────────────────────────────────── */
export default function ProductTour({ onDone }: { onDone: () => void }) {
  const { lang, addAccount, setDailyGoalTarget, setDailyMaxLoss, setModal } = useStore();
  const navigate = useNavigate();
  const isHe = lang === 'he';

  const [phase, setPhase] = useState<Phase>('setup');
  const driverRef = useRef<Driver | null>(null);

  // Setup form state
  const [accName, setAccName] = useState('');
  const [accType, setAccType] = useState<AccountType>('prop_firm');
  const [accBalance, setAccBalance] = useState('');
  const [goalTarget, setGoalTarget] = useState('');
  const [goalMaxLoss, setGoalMaxLoss] = useState('');

  /* Save setup data and advance to tour */
  const startTour = () => {
    if (accName.trim()) {
      const account: Account = {
        id: crypto.randomUUID(),
        name: accName.trim(),
        account_type: accType,
        broker: 'manual',
        initial_balance: parseFloat(accBalance) || 0,
        currency: 'USD',
        is_active: true,
      };
      addAccount(account);
    }
    if (parseFloat(goalTarget) > 0) setDailyGoalTarget(parseFloat(goalTarget));
    if (parseFloat(goalMaxLoss) > 0) setDailyMaxLoss(parseFloat(goalMaxLoss));

    navigate('/dashboard');
    setTimeout(() => setPhase('driver-dashboard'), 400);
  };

  const finish = (action?: 'trade' | 'import' | 'dashboard') => {
    driverRef.current?.destroy();
    onDone();
    if (action === 'trade') {
      navigate('/dashboard');
      setTimeout(() => setModal({ type: 'new' }), 200);
    } else if (action === 'import') {
      navigate('/dashboard/settings');
    } else {
      navigate('/dashboard');
    }
  };

  /* ── Driver.js phase runner ────────────────────────────────────── */
  useEffect(() => {
    if (!phase.startsWith('driver-')) return;

    const page = phase.replace('driver-', '') as
      | 'dashboard'
      | 'dayrecap'
      | 'analytics'
      | 'playbook'
      | 'prophq'
      | 'settings';

    const STEPS: Record<typeof page, DriveStep[]> = {
      dashboard: [
        {
          element: '#tour-statsbar',
          popover: {
            title: isHe ? '📊 ביצועים בשנייה' : '📊 Performance at a Glance',
            description: isHe
              ? 'כאן תמצא את סיכום הביצועים שלך — P&L כולל, אחוז זכייה, ממוצע רווח/הפסד ועוד.'
              : 'Your overall performance summary — total P&L, win rate, average win/loss and more.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#tour-daily-goal',
          popover: {
            title: isHe ? '🎯 יעד יומי' : '🎯 Daily Goal Bar',
            description: isHe
              ? 'סרגל היעד היומי מראה כמה רחוק אתה מיעד הרווח ומגבול ההפסד היומי. הגדר אותם בהגדרות.'
              : 'Tracks how far you are from your daily profit target and max loss limit. Set them in Settings.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#tour-calendar',
          popover: {
            title: isHe ? '🗓 לוח שנה מסחרי' : '🗓 Trading Calendar',
            description: isHe
              ? 'ראה את ה-P&L וה-WR% שלך לכל יום בחודש בלחיצה אחת. צבע ירוק = יום מנצח, אדום = יום מפסיד.'
              : 'See your P&L and win rate for every trading day. Green = winning day, red = losing day.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#tour-equity-chart',
          popover: {
            title: isHe ? '📈 עקומת הון' : '📈 Equity Curve',
            description: isHe
              ? 'גרף שמציג את ה-P&L המצטבר שלך לאורך החודש — גבוה = ביצועים טובים.'
              : 'Shows your cumulative P&L over the current month — higher is better.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#tour-new-trade',
          popover: {
            title: isHe ? '✏️ הוסף עסקה' : '✏️ Add a Trade',
            description: isHe
              ? 'לחץ כאן בכל עת כדי לרשום עסקה חדשה. תוכל גם לייבא אוטומטית מ-TopstepX או בקובץ CSV מכל ברוקר. כל עסקה ניתנת ל-Replay על גרף נרות אמיתי כדי ללמוד מהכניסה והיציאה שלך.'
              : 'Click here anytime to log a new trade. You can also auto-import from TopstepX, or import a CSV from any broker. Every trade can be replayed on a real candlestick chart to study your entry and exit.',
            side: 'bottom',
            align: 'end',
          },
        },
      ],
      dayrecap: [
        {
          element: '#tour-day-recap',
          popover: {
            title: isHe ? '📓 ריקאפ יומי' : '📓 Day Recap',
            description: isHe
              ? 'סקירה יומית/שבועית של כל יום מסחר — עקומת הון תוך-יומית, KPIs, יעד יומי, פתק יומי ודירוג דיסציפלינה (1–5). מצוין לבניית רוטינה של חזרה על המסחר.'
              : 'A day-by-day and week-by-week review of every trading day — intraday equity curve, KPIs, daily goal, a daily journal note and a discipline rating (1–5). Perfect for building a review routine.',
            side: 'bottom',
            align: 'start',
          },
        },
      ],
      analytics: [
        {
          element: '#tour-analytics-tabs',
          popover: {
            title: isHe ? '📊 8 גרפי אנליטיקה' : '📊 8 Analytics Charts',
            description: isHe
              ? 'Equity Curve, Drawdown, P&L לפי יום ולפי סמל, Heatmap חודשי, התפלגות, Risk/Reward Scatter וניתוח רצפים. כולם ב-Canvas, ללא ספריות חיצוניות.'
              : 'Equity Curve, Drawdown, P&L by day & symbol, Monthly Heatmap, Distribution, R/R Scatter, and Streak Analysis — all built with Canvas.',
            side: 'bottom',
            align: 'start',
          },
        },
      ],
      playbook: [
        {
          element: '#tour-playbook',
          popover: {
            title: isHe ? '📖 פלייבוק' : '📖 Playbook',
            description: isHe
              ? "בנה את הסטאפים שלך — שם, סמלים, כללי כניסה/יציאה/סיכון וצ'קליסט. כל סטאפ מקבל אחוז זכייה, תוחלת ועקומת הון משלו, ובזמן רישום עסקה תוכל לתייג אותה לסטאפ ולעבור על הצ'קליסט."
              : 'Build your trading setups — name, symbols, entry/exit/risk rules and a checklist. Each setup gets its own win rate, expectancy and equity curve, and when logging a trade you can tag it to a setup and run its pre-trade checklist.',
            side: 'bottom',
            align: 'start',
          },
        },
      ],
      prophq: [
        {
          element: '#tour-prop-hq',
          popover: {
            title: isHe ? '🏆 Prop HQ' : '🏆 Prop HQ',
            description: isHe
              ? 'מרכז הבקרה לחשבונות Prop Firm — Drawdown, יעד רווח וגבול הפסד יומי בזמן אמת, התראות פסילה, מד עקביות משיכות ולשונית ניהול עסקי (משיכות מול עמלות, CPA, תובנות). מאחד את Prop Firm Tracker וה-Business Manager הישנים.'
              : 'Your command center for prop firm accounts — real-time drawdown, profit target and daily loss limit, breach alerts, payout-consistency meter, and a Business tab (payouts vs fees, CPA, insights). Unifies the old Prop Firm Tracker and Business Manager.',
            side: 'top',
            align: 'start',
          },
        },
      ],
      settings: [
        {
          element: '#tour-strategy-section',
          popover: {
            title: isHe ? '🧠 אסטרטגיות מסחר' : '🧠 Trading Strategies',
            description: isHe
              ? "צור את האסטרטגיות שלך עם checkboxes ושדות טקסט. כשתרשום עסקה תוכל לבחור אסטרטגיה ולמלא את הצ'קליסט — זה עוזר לשמור על דיסציפלינה ולנתח מה עובד."
              : 'Build strategies with custom checklists and text fields. When logging a trade, select a strategy and fill in the checklist — great for tracking discipline and what actually works.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#tour-broker-section',
          popover: {
            title: isHe ? '🔄 ייבוא אוטומטי' : '🔄 Auto Import',
            description: isHe
              ? 'חבר TopstepX (API Key) — המערכת מוצאת את החשבונות שלך, מייבאת היסטוריה ומסנכרנת עסקאות חדשות אוטומטית כל 30 דקות. מברוקרים אחרים (כולל Tradovate) מייבאים CSV. גם כאן מגדירים טווח Break-Even ואזור זמן.'
              : 'Connect TopstepX (API key) — TraderYo finds your accounts, imports history and auto-syncs new trades every 30 minutes. Other brokers (including Tradovate) import via CSV. This page is also where you set your Break-Even range and timezone.',
            side: 'top',
            align: 'start',
          },
        },
      ],
    };

    /* Next phase after each page tour */
    const nextPhase: Record<typeof page, Phase> = {
      dashboard: 'bridge-dayrecap',
      dayrecap: 'bridge-analytics',
      analytics: 'bridge-playbook',
      playbook: 'bridge-prophq',
      prophq: 'bridge-settings',
      settings: 'done',
    };

    const d = driver({
      showProgress: true,
      progressText: isHe ? '{{current}} מתוך {{total}}' : '{{current}} of {{total}}',
      nextBtnText: isHe ? 'הבא →' : 'Next →',
      prevBtnText: isHe ? '→ הקודם' : '← Back',
      doneBtnText: isHe ? 'סיים ←' : 'Done →',
      steps: STEPS[page],
      onDestroyStarted: () => {
        // Allow closing without confirmation
        d.destroy();
      },
      onDestroyed: () => {
        driverRef.current = null;
        setPhase(nextPhase[page]);
      },
    });

    driverRef.current = d;
    // Small delay so the page has time to render
    const t = setTimeout(() => d.drive(), 350);
    return () => {
      clearTimeout(t);
      d.destroy();
    };
  }, [phase]);

  /* ── Render ────────────────────────────────────────────────────── */

  if (phase === 'setup') {
    return (
      <Overlay isHe={isHe}>
        {/* Welcome header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src="/logo-icon-sm.webp"
            alt="TraderYo"
            style={{
              width: 64,
              height: 64,
              borderRadius: 12,
              objectFit: 'contain',
              marginBottom: 12,
            }}
          />
          <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.5 }}>
            {isHe ? '👋 ברוך הבא ל-TraderYo!' : '👋 Welcome to TraderYo!'}
          </h2>
          <p style={{ color: T2, fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            {isHe
              ? "בוא נגדיר את החשבון הראשון שלך ואז נסייר בכל הפיצ'רים — לוקח פחות מ-2 דקות."
              : "Let's set up your first account and then take a quick tour — under 2 minutes."}
          </p>
        </div>

        {/* Account setup */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
          <Input
            label={isHe ? 'שם החשבון' : 'Account name'}
            value={accName}
            onChange={setAccName}
            placeholder={isHe ? 'למשל: Topstep 50K, Live NQ...' : 'e.g. Topstep 50K, Live NQ...'}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, color: T2, fontWeight: 500 }}>
              {isHe ? 'סוג חשבון' : 'Account type'}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(
                [
                  ['prop_firm', isHe ? 'ממומן' : 'Funded'],
                  ['personal', isHe ? 'לייב' : 'Live'],
                  ['demo', isHe ? 'תיק מבחן' : 'Eval'],
                ] as [AccountType, string][]
              ).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setAccType(val)}
                  style={{
                    flex: 1,
                    padding: '9px 8px',
                    borderRadius: 8,
                    border: `1px solid ${accType === val ? G : BD}`,
                    background: accType === val ? 'var(--primary-soft)' : S2,
                    color: accType === val ? G : T2,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: accType === val ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input
              label={isHe ? 'יתרה התחלתית ($)' : 'Starting balance ($)'}
              value={accBalance}
              onChange={setAccBalance}
              type="number"
              placeholder="50000"
            />
            <Input
              label={isHe ? 'יעד רווח יומי ($)' : 'Daily profit target ($)'}
              value={goalTarget}
              onChange={setGoalTarget}
              type="number"
              placeholder="500"
            />
          </div>

          <Input
            label={isHe ? 'מקסימום הפסד יומי ($)' : 'Daily max loss ($)'}
            value={goalMaxLoss}
            onChange={setGoalMaxLoss}
            type="number"
            placeholder="200"
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
            <Btn variant="ghost" onClick={() => finish('dashboard')}>
              {isHe ? 'דלג על הכל' : 'Skip everything'}
            </Btn>
            <Btn onClick={startTour} disabled={!accName.trim()}>
              {isHe ? 'התחל סיור ←' : 'Start tour →'}
            </Btn>
          </div>
          <button
            onClick={() => {
              navigate('/dashboard');
              setTimeout(() => setPhase('driver-dashboard'), 400);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: T2,
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 4,
            }}
          >
            {isHe
              ? 'דלג על הקמת החשבון — הראה לי את הסיור'
              : 'Skip account setup — just show me the tour'}
          </button>
        </div>
      </Overlay>
    );
  }

  if (phase === 'bridge-dayrecap') {
    return (
      <BridgeModal
        icon="📓"
        title={isHe ? 'ריקאפ יומי' : 'Day Recap'}
        desc={
          isHe
            ? 'סקור כל יום מסחר לעומק — עקומת הון תוך-יומית, KPIs, פתק יומי ודירוג דיסציפלינה.'
            : 'Review each trading day in depth — intraday equity curve, KPIs, a journal note and a discipline rating.'
        }
        btnLabel={isHe ? 'בואו נראה ←' : "Let's see →"}
        onNext={() => {
          navigate('/dashboard/day-recap');
          setTimeout(() => setPhase('driver-dayrecap'), 400);
        }}
        onSkip={() => finish('dashboard')}
        isHe={isHe}
      />
    );
  }

  if (phase === 'bridge-analytics') {
    return (
      <>
        <BridgeModal
          icon="📊"
          title={isHe ? 'עכשיו נסתכל על Analytics' : "Now let's explore Analytics"}
          desc={
            isHe
              ? '8 גרפים Canvas שמנתחים את ביצועי המסחר שלך לעומק — equity, drawdown, heatmap ועוד.'
              : '8 Canvas charts that analyse your trading performance in depth — equity, drawdown, heatmap and more.'
          }
          btnLabel={isHe ? 'בואו נראה ←' : "Let's go →"}
          onNext={() => {
            navigate('/dashboard/analytics');
            setTimeout(() => setPhase('driver-analytics'), 400);
          }}
          onSkip={() => finish('dashboard')}
          isHe={isHe}
        />
      </>
    );
  }

  if (phase === 'bridge-playbook') {
    return (
      <BridgeModal
        icon="📖"
        title={isHe ? 'פלייבוק' : 'Playbook'}
        desc={
          isHe
            ? "נהל את הסטאפים שלך — כללים, צ'קליסט, אחוז זכייה ותוחלת לכל סטאפ. תייג עסקאות לסטאפים כדי לדעת מה באמת עובד."
            : 'Manage your trading setups — rules, checklist, win rate and expectancy per setup. Tag trades to setups to learn what actually works.'
        }
        btnLabel={isHe ? 'בואו נראה ←' : "Let's see →"}
        onNext={() => {
          navigate('/dashboard/playbook');
          setTimeout(() => setPhase('driver-playbook'), 400);
        }}
        onSkip={() => finish('dashboard')}
        isHe={isHe}
      />
    );
  }

  if (phase === 'bridge-prophq') {
    return (
      <BridgeModal
        icon="🏆"
        title={isHe ? 'Prop HQ' : 'Prop HQ'}
        desc={
          isHe
            ? 'מרכז הבקרה לחשבונות Prop — drawdown ויעדים בזמן אמת, התראות פסילה, ולשונית ניהול עסקי (משיכות מול עמלות, CPA, תובנות).'
            : 'Your prop firm command center — real-time drawdown and targets, breach alerts, and a Business tab (payouts vs fees, CPA, insights).'
        }
        btnLabel={isHe ? 'בואו נראה ←' : "Let's see →"}
        onNext={() => {
          navigate('/dashboard/prop-hq');
          setTimeout(() => setPhase('driver-prophq'), 400);
        }}
        onSkip={() => finish('dashboard')}
        isHe={isHe}
      />
    );
  }

  if (phase === 'bridge-settings') {
    return (
      <BridgeModal
        icon="⚙️"
        title={isHe ? 'אסטרטגיות וחיבור ברוקר' : 'Strategies & Broker Connection'}
        desc={
          isHe
            ? 'צור את האסטרטגיות שלך וחבר את TopstepX לייבוא אוטומטי של עסקאות.'
            : 'Create your trading strategies and connect TopstepX for automatic trade import.'
        }
        btnLabel={isHe ? 'בואו נגדיר ←' : "Let's set up →"}
        onNext={() => {
          navigate('/dashboard/settings');
          setTimeout(() => setPhase('driver-settings'), 400);
        }}
        onSkip={() => finish('dashboard')}
        isHe={isHe}
      />
    );
  }

  if (phase === 'done') {
    return (
      <Overlay isHe={isHe}>
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 22,
          }}
        >
          <div style={{ fontSize: 56 }}>🚀</div>
          <div>
            <h3 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>
              {isHe ? 'הכל מוכן!' : "You're all set!"}
            </h3>
            <p style={{ color: T2, fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              {isHe
                ? 'TraderYo מוכן לעבוד בשבילך. איך תרצה להתחיל?'
                : 'TraderYo is ready to work for you. How do you want to start?'}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              width: '100%',
              maxWidth: 320,
            }}
          >
            <button
              onClick={() => finish('trade')}
              style={{
                background: G,
                color: '#fff',
                border: 'none',
                borderRadius: 999,
                padding: '13px 24px',
                fontSize: 15,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              {isHe ? '✏️ הוסף עסקה ראשונה' : '✏️ Add first trade'}
            </button>
            <button
              onClick={() => finish('import')}
              style={{
                background: S2,
                color: 'var(--t1)',
                border: `1px solid ${BD}`,
                borderRadius: 999,
                padding: '12px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isHe ? '🔄 ייבא מ-TopstepX / CSV' : '🔄 Import from TopstepX / CSV'}
            </button>
            <button
              onClick={() => finish('dashboard')}
              style={{
                background: 'transparent',
                color: T2,
                border: 'none',
                padding: '10px 24px',
                fontSize: 13,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              {isHe ? 'עבור ללוח הבקרה' : 'Go to dashboard'}
            </button>
          </div>
        </div>
      </Overlay>
    );
  }

  /* driver-* phases: just render the skip button — Driver.js handles the rest */
  if (phase.startsWith('driver-')) {
    return <SkipButton onClick={() => finish('dashboard')} isHe={isHe} />;
  }

  return null;
}
